<template>
    <div class="statement_card">
        <div class="card_inner">
            <div class="card_header is-flex is-justify-content-space-between is-align-items-center">
                <span class="period_label">{{ periodLabel }}</span>
                <div class="pills">
                    <span v-if="hasMom" class="pill pill--mom">{{ formatPct(headline.mom) }} MoM</span>
                    <span v-if="hasYoy" class="pill pill--yoy">{{ formatPct(headline.yoy) }} YoY</span>
                </div>
            </div>
            <p class="main_amount">{{ formatMoney(headline.amount) }}</p>
        </div>

        <div class="statement">
            <div class="statement_header is-flex is-align-items-center">
                <PieChartIcon class="statement_icon" />
                <span class="statement_title">Costos</span>
            </div>

            <ul class="statement_list">
                <template v-for="group in groups">
                    <li :key="group.level_2" class="group_header"
                        role="button"
                        tabindex="0"
                        :aria-expanded="String(isExpanded(group.level_2))"
                        @click="toggle(group.level_2)"
                        @keydown.enter.prevent="toggle(group.level_2)"
                        @keydown.space.prevent="toggle(group.level_2)">
                        <span class="group_label">{{ group.level_2 }}</span>
                        <span class="group_right is-flex is-align-items-center">
                            <span class="group_amount">{{ formatMoney(group.amount) }}</span>
                            <ArrowDown class="group_chevron"
                                :class="{ 'rotate-icon': isExpanded(group.level_2) }" />
                        </span>
                    </li>

                    <template v-if="isExpanded(group.level_2)">
                        <li v-for="child in group.children"
                            :key="group.level_2 + '|' + child.account"
                            class="child_row">
                            <span class="child_account">{{ child.account }}</span>
                            <span class="child_amount">{{ formatMoney(child.amount) }}</span>
                        </li>
                    </template>

                    <li :key="group.level_2 + '|margin'" class="margin_row">
                        <span class="margin_left is-flex is-align-items-center">
                            <ChartLine class="margin_icon" />
                            <span class="margin_label">{{ group.margin.label }}</span>
                        </span>
                        <span class="margin_right is-flex is-align-items-center">
                            <span class="margin_amount">{{ formatMoney(group.margin.amount) }}</span>
                            <span v-if="group.margin.pct != null" class="margin_badge">
                                {{ formatMarginPct(group.margin.pct) }}
                            </span>
                        </span>
                    </li>
                </template>
            </ul>
        </div>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatVariation } from "@/utils/percentage";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";
import ChartLine from "@/components/svg/ChartLine.vue";
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
    name: "CostStatementCard",
    components: { PieChartIcon, ChartLine, ArrowDown },
    props: {
        periodLabel: { type: String, default: "" },
        // { amount, mom, yoy } — costo total del mes + comparables.
        headline: {
            type: Object,
            required: true,
            validator: (v) => v && typeof v.amount === "number",
        },
        // { sales_total, total_costs, groups: [{ level_2, amount, children,
        // margin: { label, amount, pct } }] }.
        statement: {
            type: Object,
            default: () => ({ groups: [] }),
        },
    },
    data() {
        // level_2 de los grupos actualmente expandidos. Se rellena desde el
        // statement (todos abiertos por defecto) — el detalle del estado es la
        // lectura principal de esta card, no un extra colapsado.
        return { expandedGroups: [] };
    },
    computed: {
        groups() {
            return this.statement?.groups ?? [];
        },
        hasMom() {
            return this.headline.mom != null;
        },
        hasYoy() {
            return this.headline.yoy != null;
        },
    },
    watch: {
        // Cada refetch (cambio de mes o de filtros) reabre todos los grupos —
        // estado fresco, sin arrastrar colapsos de una vista anterior.
        statement: {
            immediate: true,
            handler() {
                this.expandedGroups = this.groups.map((g) => g.level_2);
            },
        },
    },
    methods: {
        formatMoney(v) {
            return parseToMoney(v);
        },
        // Mismo formato que HeadlineMonthCard (Ventas): color del pill conduce
        // la jerarquía MoM/YoY; el "-" se conserva para los negativos.
        formatPct(pct) {
            return formatVariation(pct, { dash: "" });
        },
        // Badge de margen: porcentaje entero, coherente con los shares enteros
        // del ranking dentro del mismo modal.
        formatMarginPct(pct) {
            return `${Math.round(pct * 100)}%`;
        },
        isExpanded(level2) {
            return this.expandedGroups.includes(level2);
        },
        toggle(level2) {
            const idx = this.expandedGroups.indexOf(level2);
            if (idx > -1) this.expandedGroups.splice(idx, 1);
            else this.expandedGroups.push(level2);
        },
    },
};
</script>

<style scoped lang="scss">
.statement_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    padding: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    // La card es el clamp: el headline queda fijo y el estado scrollea dentro.
    overflow: hidden;
}

// Inner surface: agrupa período + pills + monto total sobre --report-surface,
// separándolo del estado que vive en el outer (--report-bg). Mismo patrón que
// HeadlineMonthCard de Ventas.
.card_inner {
    padding: 20px;
    background: var(--report-surface);
    border-radius: 12px;
    border: 1px solid var(--report-border);
    flex-shrink: 0;
}

.card_header {
    margin-bottom: 12px;
}

.period_label {
    color: var(--report-text-muted);
    font-size: 14px;
    font-weight: 500;
}

.pills {
    display: flex;
    gap: 6px;
}

.pill {
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid;
}

// MoM filled / YoY outline — misma familia cromática (accent del tab),
// diferenciadas por tratamiento, no por color (system.md). Neutral respecto al
// signo: en Costos un alza no es "verde positivo".
.pill--mom {
    background: var(--report-accent-bg);
    color: var(--report-accent-text);
    border-color: var(--report-accent-border);
}

.pill--yoy {
    background: transparent;
    color: var(--report-accent-text);
    border-color: var(--report-accent-border);
}

.main_amount {
    font-size: 36px;
    font-weight: 600;
    color: var(--report-text);
    letter-spacing: -0.5px;
    margin: 0;
    line-height: 1.1;
}

.statement {
    padding: 16px 4px 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.statement_header {
    margin-bottom: 8px;
    color: var(--report-text);
    flex-shrink: 0;

    .statement_icon {
        width: 16px;
        height: 16px;
        margin-right: 8px;
        flex-shrink: 0;
        color: var(--report-text);
    }

    .statement_title {
        font-size: 14px;
        font-weight: 600;
    }
}

.statement_list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

// Encabezado de grupo L2 (Costos Productos/Variables/Fijos): label + monto
// bold + chevron. Toda la fila es el toggle.
.group_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 2px;
    border-top: 1px solid var(--report-divider-soft);
    cursor: pointer;
    font-size: 14px;

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
        border-radius: 6px;
    }

    .group_label {
        color: var(--report-text);
        font-weight: 600;
    }

    .group_right {
        gap: 8px;
    }

    .group_amount {
        color: var(--report-text);
        font-weight: 600;
    }

    .group_chevron {
        width: 13px;
        height: 13px;
        color: var(--report-text-muted-soft);
        transition: transform 0.2s ease;
    }

    .rotate-icon {
        transform: rotate(180deg);
    }
}

// Hijo L3: indentado, muted, sin borde — vive bajo su grupo.
.child_row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 2px 7px 20px;
    font-size: 13px;

    .child_account {
        color: var(--report-text-muted);
    }

    .child_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }
}

// Fila de margen — la conclusión de cada grupo. Color accent del tab + badge
// con el % para que destaque sobre los costos. Mismo lenguaje visual que el
// margin_badge del summary de CostosTab.
.margin_row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 2px;
    font-size: 13px;

    .margin_left {
        gap: 6px;
    }

    .margin_icon {
        width: 14px;
        height: 14px;
        color: var(--report-accent-text);
    }

    .margin_label {
        color: var(--report-accent-text);
        font-weight: 600;
    }

    .margin_right {
        gap: 8px;
    }

    .margin_amount {
        color: var(--report-accent-text);
        font-weight: 600;
    }

    .margin_badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 999px;
        background: var(--report-accent-bg);
        color: var(--report-accent-text);
        border: 1px solid var(--report-accent-border);
        font-size: 12px;
        font-weight: 700;
    }
}
</style>
